import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { networkManager } from '../api/NetworkManager';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  Loader2,
  Copy,
  Check,
  Users,
  FileEdit,
  LogOut,
  MessageSquare,
  Send,
  Camera,
  UserPlus,
  Search,
  X,
  CalendarClock,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Trophy,
  Play,
  GitBranch,
  ExternalLink,
  FileDown,
} from 'lucide-react';
import axios from 'axios';
import { findSlotForTeam, slotRelativeLabel, formatSlotDateTime } from '../lib/schedule';

interface Member {
  sub: string;
  name: string;
  email: string;
  role: string;
}

interface Feedback {
  feedbackId: string;
  mentorName: string;
  content: string;
  section: string;
  type: string;
  createdAt: string;
}

interface TeamDetail {
  teamId: string;
  name: string;
  themeId: string | null;
  inviteCode: string;
  members: Member[];
  teamPhotoUrl?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface EventTheme {
  id: string;
  name: string;
  description: string;
}

const FEEDBACK_SECTIONS = [
  { value: 'problem', label: 'Problem Statement' },
  { value: 'solution', label: 'Solution' },
  { value: 'impact', label: 'Impact' },
  { value: 'technical', label: 'Technical Approach' },
  { value: 'general', label: 'General / Overall' },
];

const FEEDBACK_TYPES = [
  { value: 'direction', label: 'Guidance', color: 'bg-blue-100 text-blue-700' },
  { value: 'blocker', label: 'Blocker', color: 'bg-red-100 text-red-700' },
  { value: 'encouragement', label: 'Encouragement', color: 'bg-green-100 text-green-700' },
];

export function TeamDashboardPage() {
  const { eventId, teamId } = useParams();
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const canGiveFeedback = can('feedback:give');
  const canViewSubmission = can('submission:view-all');
  const canManageTeams = can('submission:view-all'); // admins + organizers
  const canScore = can('score:submit');
  const canSummarize = can('ai:summarize');
  // Judges and organizers get a prev/next pager across teams.
  const canPagerTeams = canViewSubmission || canScore;

  const [team, setTeam] = useState<TeamDetail | null>(null);
  const [themes, setThemes] = useState<EventTheme[]>([]);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [submissionProgress, setSubmissionProgress] = useState(0);
  const [submissionStatus, setSubmissionStatus] = useState<string>('draft');
  const [submission, setSubmission] = useState<{
    title?: string;
    problem?: string;
    solution?: string;
    impact?: string;
    technicalApproach?: string;
    productSummary?: string;
    productSummaryGeneratedAt?: string;
    demoUrl?: string;
    repoUrl?: string;
    fileUrls?: string[];
  } | null>(null);
  const [allTeamsForPager, setAllTeamsForPager] = useState<Array<{ teamId: string; name: string }>>([]);
  // On-demand AI judging summary (strengths / risks / questions).
  // Distinct from the saved one-line product summary on the submission.
  const [judgeSummarizing, setJudgeSummarizing] = useState(false);
  const [judgeSummary, setJudgeSummary] = useState<{
    summary: string;
    strengths: string[];
    risks: string[];
    questions: string[];
  } | null>(null);
  const [judgeSummaryExpanded, setJudgeSummaryExpanded] = useState(true);
  const [judgeSummaryError, setJudgeSummaryError] = useState<string | null>(null);
  const [mentorsEnabled, setMentorsEnabled] = useState(true);
  const [schedule, setSchedule] = useState<import('../lib/schedule').EventSchedule | null>(null);
  const [notifications, setNotifications] = useState<Array<{ notificationId: string; message: string; senderName: string; sentAt: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [leaving, setLeaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  // Photo upload state
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');

  // Admin member management state
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState<Array<{ sub: string; yumId: string; name: string; email: string }>>([]);
  const [memberSearching, setMemberSearching] = useState(false);
  const [addingMember, setAddingMember] = useState<string | null>(null);
  const [removingMember, setRemovingMember] = useState<string | null>(null);

  // Feedback form state
  const [fbSection, setFbSection] = useState('general');
  const [fbType, setFbType] = useState('direction');
  const [fbContent, setFbContent] = useState('');
  const [fbSending, setFbSending] = useState(false);
  const [fbError, setFbError] = useState('');
  const [fbSuccess, setFbSuccess] = useState(false);

  useEffect(() => {
    Promise.all([
      networkManager.get<{ status: boolean; data: TeamDetail }>(
        `/api/events/${eventId}/teams/${teamId}`,
      ),
      networkManager.get<{ status: boolean; data: Feedback[] }>(
        `/api/events/${eventId}/teams/${teamId}/feedback`,
      ).catch(() => ({ data: [] as Feedback[] })),
      networkManager.get<{ status: boolean; data: { progress?: number; status?: string; title?: string; problem?: string; solution?: string; impact?: string; technicalApproach?: string; productSummary?: string; productSummaryGeneratedAt?: string; demoUrl?: string; repoUrl?: string; fileUrls?: string[] } }>(
        `/api/events/${eventId}/teams/${teamId}/submissions`,
      ).catch(() => ({ data: null })),
      networkManager.get<{ status: boolean; data: { themes: EventTheme[]; settings?: { enableMentors?: boolean }; schedule?: import('../lib/schedule').EventSchedule } }>(
        `/api/events/${eventId}`,
      ).catch((): { data: { themes: EventTheme[]; settings?: { enableMentors?: boolean }; schedule?: import('../lib/schedule').EventSchedule } } => ({ data: { themes: [], settings: { enableMentors: true } } })),
      networkManager.get<{ status: boolean; data: Array<{ notificationId: string; message: string; senderName: string; sentAt: string }> }>(
        `/api/bulk/notifications/${eventId}`,
      ).catch(() => ({ data: [] })),
    ])
      .then(([teamRes, feedbackRes, subRes, eventRes, notifRes]) => {
        setTeam(teamRes.data);
        setFeedback(feedbackRes.data);
        setThemes(eventRes.data.themes ?? []);
        setMentorsEnabled(eventRes.data.settings?.enableMentors !== false);
        setSchedule(eventRes.data.schedule ?? null);
        setNotifications(notifRes.data ?? []);
        // Calculate progress from submission fields
        if (subRes.data) {
          const sub = subRes.data;
          setSubmissionStatus(sub.status ?? 'draft');
          setSubmission({
            title: sub.title,
            problem: sub.problem,
            solution: sub.solution,
            impact: sub.impact,
            technicalApproach: sub.technicalApproach,
            productSummary: sub.productSummary,
            productSummaryGeneratedAt: sub.productSummaryGeneratedAt,
            demoUrl: sub.demoUrl,
            repoUrl: sub.repoUrl,
            fileUrls: sub.fileUrls,
          });
          const fields = ['title', 'problem', 'solution', 'impact', 'technicalApproach'] as const;
          const filled = fields.filter((f) => ((sub as Record<string, string>)[f] ?? '').trim().length > 0).length;
          setSubmissionProgress(Math.round((filled / fields.length) * 100));
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [eventId, teamId]);

  // Load team list for the prev/next pager (judges + organizers only).
  useEffect(() => {
    if (!eventId || !canPagerTeams) return;
    networkManager
      .get<{ status: boolean; data: Array<{ teamId: string; name: string }> }>(
        `/api/events/${eventId}/teams`,
      )
      .then((res) => setAllTeamsForPager(res.data))
      .catch(() => setAllTeamsForPager([]));
  }, [eventId, canPagerTeams]);

  async function handleJudgeSummarize() {
    if (!eventId || !teamId) return;
    setJudgeSummarizing(true);
    setJudgeSummaryError(null);
    try {
      const res = await networkManager.post<{
        status: boolean;
        data: { summary: string; strengths: string[]; risks: string[]; questions: string[] };
      }>('/api/ai/summarize', { eventId, teamId });
      setJudgeSummary(res.data);
      setJudgeSummaryExpanded(true);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'AI summarization failed — please try again later';
      setJudgeSummaryError(msg);
    } finally {
      setJudgeSummarizing(false);
    }
  }

  const [memberSearchDone, setMemberSearchDone] = useState(false);

  async function handleSearchMembers() {
    if (memberSearch.trim().length < 2) return;
    setMemberSearching(true);
    setMemberSearchDone(false);
    try {
      const res = await networkManager.get<{ status: boolean; data: Array<{ sub: string; yumId: string; name: string; email: string }> }>(
        `/api/users/search?q=${encodeURIComponent(memberSearch.trim())}&limit=10`,
      );
      // Filter out users already on this team
      const existing = new Set(team?.members.map((m) => m.sub) ?? []);
      setMemberResults(res.data.filter((u) => !existing.has(u.sub) && !existing.has(u.yumId)));
    } catch {
      setMemberResults([]);
    } finally {
      setMemberSearching(false);
      setMemberSearchDone(true);
    }
  }

  async function handleAddMember(u: { sub: string; yumId: string; name: string; email: string }) {
    if (!team) return;
    setAddingMember(u.sub || u.yumId);
    try {
      const res = await networkManager.post<{ status: boolean; data: TeamDetail }>(
        `/api/events/${eventId}/teams/${teamId}/members`,
        { sub: u.yumId || u.sub, name: u.name, email: u.email },
      );
      setTeam(res.data);
      setMemberResults((prev) => prev.filter((r) => r.sub !== u.sub && r.yumId !== u.yumId));
      setMemberSearch('');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to add member';
      setError(msg);
    } finally {
      setAddingMember(null);
    }
  }

  async function handleRemoveMember(memberSub: string) {
    if (!team) return;
    setRemovingMember(memberSub);
    try {
      await networkManager.delete(`/api/events/${eventId}/teams/${teamId}/members/${memberSub}`);
      setTeam({ ...team, members: team.members.filter((m) => m.sub !== memberSub) });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to remove member';
      setError(msg);
    } finally {
      setRemovingMember(null);
    }
  }

  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !eventId || !teamId) return;

    // Validate
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setPhotoError('Only PNG, JPG, or GIF images are supported');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('Image must be under 5 MB');
      return;
    }

    setPhotoUploading(true);
    setPhotoError('');
    try {
      // 1. Get presigned URL
      const { data: uploadData } = await networkManager.post<{ status: boolean; data: { uploadUrl: string; photoUrl: string } }>(
        `/api/events/${eventId}/teams/${teamId}/team-photo/upload`,
        { fileName: file.name, contentType: file.type },
      );

      // 2. Upload directly to S3
      await axios.put(uploadData.uploadUrl, file, {
        headers: { 'Content-Type': file.type },
      });

      // 3. Confirm upload
      await networkManager.put(
        `/api/events/${eventId}/teams/${teamId}/team-photo`,
        { photoUrl: uploadData.photoUrl },
      );

      // Update local state
      setTeam((prev) => prev ? { ...prev, teamPhotoUrl: uploadData.photoUrl } : prev);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data?.message
        ?? (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? 'Failed to upload photo';
      setPhotoError(msg);
    } finally {
      setPhotoUploading(false);
      // Reset the input so the same file can be re-selected
      e.target.value = '';
    }
  }

  async function handleSubmitFeedback(e: React.FormEvent) {
    e.preventDefault();
    if (!fbContent.trim()) {
      setFbError('Feedback content is required');
      return;
    }
    if (fbContent.trim().length > 2000) {
      setFbError('Feedback must be 2000 characters or fewer');
      return;
    }

    setFbSending(true);
    setFbError('');
    setFbSuccess(false);
    try {
      const res = await networkManager.post<{ status: boolean; data: Feedback }>(
        `/api/events/${eventId}/teams/${teamId}/feedback`,
        { section: fbSection, type: fbType, content: fbContent.trim() },
      );
      setFeedback((prev) => [res.data, ...prev]);
      setFbContent('');
      setFbSuccess(true);
      setTimeout(() => setFbSuccess(false), 3000);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to submit feedback';
      setFbError(msg);
    } finally {
      setFbSending(false);
    }
  }

  async function handleCopyInviteCode() {
    if (!team) return;
    try {
      await navigator.clipboard.writeText(team.inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback: do nothing
    }
  }

  async function handleLeaveTeam() {
    if (!confirm('Are you sure you want to leave this team?')) return;
    setLeaving(true);
    setError('');
    try {
      await networkManager.post(`/api/events/${eventId}/teams/${teamId}/leave`);
      navigate(`/events/${eventId}`);
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to leave team. Please try again.';
      setError(message);
    } finally {
      setLeaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-ink-tertiary" />
      </div>
    );
  }

  if (!team) {
    return <p className="py-12 text-center text-ink-secondary">Team not found.</p>;
  }

  const submissionPercent = submissionProgress;
  const isMember = team.members.some((m) => m.sub === user?.sub);

  return (
    <div className="animate-fade-in">
      <Link to={`/events/${eventId}`} className="btn-ghost mb-4 inline-flex">
        <ArrowLeft className="h-4 w-4" /> Back to Event
      </Link>

      {error && (
        <div className="mb-4 card border-red-300 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          {/* Team Photo */}
          <div className="relative flex-shrink-0">
            {team.teamPhotoUrl ? (
              <img
                src={team.teamPhotoUrl}
                alt={`${team.name} team photo`}
                className="h-20 w-20 rounded-xl object-cover border border-gray-200"
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-xl bg-gray-100 border border-gray-200 text-2xl font-bold text-ink-tertiary">
                {team.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
              </div>
            )}
            <label className="absolute -bottom-1 -right-1 cursor-pointer rounded-full bg-white p-1 shadow border border-gray-200 hover:bg-gray-50">
              {photoUploading ? (
                <Loader2 className="h-4 w-4 animate-spin text-ink-tertiary" />
              ) : (
                <Camera className="h-4 w-4 text-ink-tertiary" />
              )}
              <input
                type="file"
                className="hidden"
                accept="image/png,image/jpeg,image/gif,image/webp"
                onChange={handlePhotoUpload}
                disabled={photoUploading}
              />
            </label>
          </div>
          <div>
          <h1 className="font-display text-3xl font-bold text-ink">{team.name}</h1>
          {photoError && (
            <p className="mt-1 text-xs text-red-600">{photoError}</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <span className="badge bg-blue-100 text-blue-700">
              {(team.themeId && themes.find((t) => t.id === team.themeId)?.name) || 'No theme'}
            </span>
            <span className="flex items-center gap-1 text-sm text-ink-secondary">
              <Users className="h-4 w-4" /> {team.members.length} members
            </span>
            <span className={`badge ${submissionStatus === 'submitted' ? 'bg-green-100 text-green-700' : submissionStatus === 'presented' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}`}>
              {submissionStatus}
            </span>
          </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Prev / Next pager — judges + organizers crowd-flow through teams */}
          {canPagerTeams && allTeamsForPager.length > 1 && (() => {
            const idx = allTeamsForPager.findIndex((t) => t.teamId === teamId);
            const prev = idx > 0 ? allTeamsForPager[idx - 1] : null;
            const next = idx >= 0 && idx < allTeamsForPager.length - 1 ? allTeamsForPager[idx + 1] : null;
            return (
              <div className="flex items-center gap-1.5 rounded-lg border border-ring bg-surface-raised p-1">
                {prev ? (
                  <Link
                    to={`/events/${eventId}/teams/${prev.teamId}`}
                    className="btn-ghost px-2 py-1 text-sm inline-flex items-center gap-1"
                    title={`Previous: ${prev.name}`}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Link>
                ) : (
                  <span className="px-2 py-1 text-ink-tertiary"><ChevronLeft className="h-4 w-4" /></span>
                )}
                {idx >= 0 && (
                  <span className="px-1 text-xs font-medium text-ink-secondary tabular-nums">
                    {idx + 1} / {allTeamsForPager.length}
                  </span>
                )}
                {next ? (
                  <Link
                    to={`/events/${eventId}/teams/${next.teamId}`}
                    className="btn-ghost px-2 py-1 text-sm inline-flex items-center gap-1"
                    title={`Next: ${next.name}`}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                ) : (
                  <span className="px-2 py-1 text-ink-tertiary"><ChevronRight className="h-4 w-4" /></span>
                )}
              </div>
            );
          })()}
          {canScore && (
            <Link
              to={`/events/${eventId}/judge/${teamId}`}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Trophy className="h-4 w-4" /> Score
            </Link>
          )}
          {isMember && (
            <button
              className="btn-ghost inline-flex items-center gap-2 text-red-600 hover:bg-red-50"
              onClick={handleLeaveTeam}
              disabled={leaving}
            >
              {leaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
              Leave Team
            </button>
          )}
        </div>
      </div>

      {/* Invite Code — only visible to team members */}
      {isMember && (
        <section className="mt-6">
          <h2 className="text-lg font-semibold text-ink">Invite Code</h2>
          <div className="mt-2 inline-flex items-center gap-2 card p-3">
            <code className="text-lg font-mono font-semibold text-ink">{team.inviteCode}</code>
            <button
              className="btn-ghost p-1"
              onClick={handleCopyInviteCode}
              title="Copy invite code"
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </div>
          <p className="mt-1 text-sm text-ink-tertiary">
            Share this code with others so they can join your team.
          </p>
        </section>
      )}

      {/* Notifications */}
      {notifications.length > 0 && (
        <section className="mt-6">
          <h2 className="text-lg font-semibold text-ink">Announcements</h2>
          <div className="mt-3 space-y-2">
            {notifications.slice(0, 5).map((n) => (
              <div key={n.notificationId} className="card border-l-4 border-purple-400 p-4">
                <p className="text-sm text-ink whitespace-pre-wrap">{n.message}</p>
                <p className="mt-2 text-xs text-ink-tertiary">
                  {n.senderName} &middot; {new Date(n.sentAt).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Demo slot — only rendered if the organizer has saved a schedule and this team is in it */}
      {schedule && teamId && (() => {
        const slot = findSlotForTeam(schedule, teamId);
        if (!slot) return null;
        const total = schedule.teamOrder.length;
        const minutes = Math.round(slot.durationSeconds / 60);
        const relative = slotRelativeLabel(slot);
        const isLive = relative === 'presenting now';
        const isDone = relative === 'already done';
        return (
          <section className="mt-6">
            <div
              className={`card p-5 ${
                isLive
                  ? 'border-2 border-purple-400 bg-purple-50'
                  : isDone
                    ? 'opacity-70'
                    : ''
              }`}
              aria-label="Your demo slot"
            >
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-subtle">
                  <CalendarClock className="h-5 w-5 text-ink" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-secondary">
                      Your demo slot
                    </h2>
                    <span
                      className={`rounded-full px-2 py-0.5 text-2xs font-semibold uppercase tracking-wider ${
                        isLive
                          ? 'bg-purple-600 text-white'
                          : isDone
                            ? 'bg-gray-200 text-ink-tertiary'
                            : 'bg-accent-subtle text-ink'
                      }`}
                    >
                      {relative}
                    </span>
                  </div>
                  <p className="mt-2 font-display text-2xl font-bold text-ink sm:text-3xl">
                    {formatSlotDateTime(slot.startsAt)}
                  </p>
                  <p className="mt-1 text-sm text-ink-secondary">
                    Slot {slot.position} of {total} · {minutes} minute{minutes === 1 ? '' : 's'}
                  </p>
                  <p className="mt-2 text-xs text-ink-tertiary">
                    Shown in your local time ({Intl.DateTimeFormat().resolvedOptions().timeZone}).
                    Order may change before the session starts.
                  </p>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Submission — full per-section read view, plus progress bar for members
          mid-draft. The same surface judges/organizers consume, so there's no
          separate "view submission" page anywhere. */}
      <section className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink">Submission</h2>
          {isMember && (
            <Link
              to={`/events/${eventId}/teams/${teamId}/submit`}
              className="btn-primary inline-flex items-center gap-2"
            >
              <FileEdit className="h-4 w-4" /> Edit Submission
            </Link>
          )}
        </div>

        {/* Members get a quick progress hint while drafting; non-members never
            see the bar. Once submitted/100%, hide for everyone — the cards
            below tell the real story. */}
        {isMember && submissionPercent < 100 && submissionStatus !== 'submitted' && submissionStatus !== 'presented' && (
          <div className="mt-3 card p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-ink-secondary">Submission progress</span>
              <span className="font-semibold text-ink">{submissionPercent}%</span>
            </div>
            <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-surface-sunken">
              <div
                className="h-full rounded-full bg-blue-600 transition-all duration-300"
                style={{ width: `${submissionPercent}%` }}
              />
            </div>
          </div>
        )}

        {/* Title + AI one-liner */}
        <div className="mt-3 card p-5">
          <h3 className="font-display text-xl font-bold text-ink">
            {submission?.title?.trim() || <span className="italic text-ink-tertiary">Untitled submission</span>}
          </h3>
          {submission?.productSummary && (
            <div className="mt-2 flex items-start gap-2 rounded-lg border border-accent/30 bg-accent-subtle/50 p-3">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              <div className="min-w-0 flex-1">
                <p className="text-2xs font-semibold uppercase tracking-wider text-ink-secondary">
                  AI summary
                </p>
                <p className="mt-0.5 text-sm italic text-ink">{submission.productSummary}</p>
              </div>
            </div>
          )}
        </div>

        {/* Lazy AI judging summary — strengths/risks/questions for judges */}
        {canSummarize && (
          <div className="mt-3">
            {!judgeSummary && (
              <button
                className="btn-secondary inline-flex items-center gap-1.5 text-sm"
                onClick={handleJudgeSummarize}
                disabled={judgeSummarizing}
              >
                {judgeSummarizing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {judgeSummarizing ? 'Generating AI Summary…' : 'AI Summary for Judges'}
              </button>
            )}
            {judgeSummaryError && (
              <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                {judgeSummaryError}
              </div>
            )}
            {judgeSummary && (
              <div className="card border-purple-200 bg-purple-50/30 p-4">
                <button
                  className="flex w-full items-center justify-between"
                  onClick={() => setJudgeSummaryExpanded(!judgeSummaryExpanded)}
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-purple-800">
                    <Sparkles className="h-4 w-4" /> AI Summary for Judges
                  </span>
                  {judgeSummaryExpanded ? (
                    <ChevronUp className="h-4 w-4 text-purple-600" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-purple-600" />
                  )}
                </button>
                {judgeSummaryExpanded && (
                  <div className="mt-3 space-y-3">
                    <p className="text-sm leading-relaxed text-ink">{judgeSummary.summary}</p>
                    {judgeSummary.strengths.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-green-700">Strengths</h4>
                        <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm text-green-800">
                          {judgeSummary.strengths.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                      </div>
                    )}
                    {judgeSummary.risks.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-amber-700">Risks</h4>
                        <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm text-amber-800">
                          {judgeSummary.risks.map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                      </div>
                    )}
                    {judgeSummary.questions.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-blue-700">Questions to Ask</h4>
                        <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm text-blue-800">
                          {judgeSummary.questions.map((q, i) => <li key={i}>{q}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Per-section cards */}
        <div className="mt-4 space-y-4">
          {([
            { key: 'problem' as const, label: 'Problem Statement' },
            { key: 'solution' as const, label: 'Solution' },
            { key: 'impact' as const, label: 'Impact' },
            { key: 'technicalApproach' as const, label: 'Technical Approach' },
          ]).map((f) => {
            const content = submission?.[f.key];
            return (
              <section key={f.key} className="card p-5">
                <h3 className="font-semibold text-ink">{f.label}</h3>
                <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-ink-secondary">
                  {content?.trim() ? (
                    content
                  ) : (
                    <span className="italic text-ink-tertiary">No content provided</span>
                  )}
                </div>
              </section>
            );
          })}
        </div>

        {/* Links & resources */}
        <section className="mt-4 card p-5">
          <h3 className="font-semibold text-ink">Links & Resources</h3>
          <div className="mt-3 space-y-2">
            {submission?.demoUrl ? (
              <a
                href={submission.demoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-ink hover:underline"
              >
                <Play className="h-4 w-4 text-ink-tertiary" />
                <span className="flex-1 truncate">{submission.demoUrl}</span>
                <ExternalLink className="h-3.5 w-3.5 shrink-0 text-ink-tertiary" />
              </a>
            ) : (
              <div className="flex items-center gap-2 text-sm italic text-ink-tertiary">
                <Play className="h-4 w-4" />
                No demo / video link provided
              </div>
            )}
            {submission?.repoUrl ? (
              <a
                href={submission.repoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-ink hover:underline"
              >
                <GitBranch className="h-4 w-4 text-ink-tertiary" />
                <span className="flex-1 truncate">{submission.repoUrl}</span>
                <ExternalLink className="h-3.5 w-3.5 shrink-0 text-ink-tertiary" />
              </a>
            ) : (
              <div className="flex items-center gap-2 text-sm italic text-ink-tertiary">
                <GitBranch className="h-4 w-4" />
                No repository link provided
              </div>
            )}
          </div>
        </section>

        {/* Attachments */}
        <section className="mt-4 card p-5">
          <h3 className="font-semibold text-ink">Attachments</h3>
          {submission?.fileUrls && submission.fileUrls.length > 0 ? (
            <ul className="mt-3 space-y-2">
              {submission.fileUrls.map((url, i) => (
                <li key={i}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-ink hover:underline"
                  >
                    <FileDown className="h-4 w-4" />
                    {url.split('/').pop() ?? `File ${i + 1}`}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-sm italic text-ink-tertiary">No attachments uploaded</p>
          )}
        </section>
      </section>

      {/* Members */}
      <section className="mt-8">
        <h2 className="text-lg font-semibold text-ink">Team Members</h2>
        <div className="mt-3 card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ring bg-surface-sunken">
                <th className="px-4 py-2 text-left font-medium text-ink-secondary">Name</th>
                <th className="px-4 py-2 text-left font-medium text-ink-secondary">Email</th>
                <th className="px-4 py-2 text-left font-medium text-ink-secondary">Role</th>
                {canManageTeams && <th className="px-4 py-2 text-right font-medium text-ink-secondary">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {team.members.map((member) => (
                <tr key={member.sub} className="border-b border-ring last:border-0">
                  <td className="px-4 py-2 text-ink">{member.name}</td>
                  <td className="px-4 py-2 text-ink-secondary">{member.email}</td>
                  <td className="px-4 py-2">
                    <span className="badge bg-gray-100 text-gray-700">{member.role}</span>
                  </td>
                  {canManageTeams && (
                    <td className="px-4 py-2 text-right">
                      <button
                        className="btn-ghost text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1"
                        onClick={() => handleRemoveMember(member.sub)}
                        disabled={removingMember === member.sub}
                      >
                        {removingMember === member.sub ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Admin: Add Member */}
        {canManageTeams && (
          <div className="mt-3 card p-4">
            <h3 className="text-sm font-semibold text-ink flex items-center gap-2">
              <UserPlus className="h-4 w-4" /> Add Member
            </h3>
            <div className="mt-2 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-tertiary" />
                <input
                  type="text"
                  className="input-base pl-9"
                  placeholder="Search by name or Yum ID..."
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchMembers()}
                />
              </div>
              <button
                className="btn-secondary text-sm"
                onClick={handleSearchMembers}
                disabled={memberSearching || memberSearch.trim().length < 2}
              >
                {memberSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
              </button>
            </div>
            {memberResults.length > 0 && (
              <div className="mt-2 space-y-1">
                {memberResults.map((u) => (
                  <div key={u.sub || u.yumId} className="flex items-center justify-between rounded-lg bg-surface-sunken p-2">
                    <div>
                      <span className="text-sm font-medium text-ink">{u.name}</span>
                      <span className="ml-2 text-xs text-ink-tertiary">{u.email}</span>
                    </div>
                    <button
                      className="btn-primary text-xs px-3 py-1 inline-flex items-center gap-1"
                      onClick={() => handleAddMember(u)}
                      disabled={addingMember === (u.sub || u.yumId)}
                    >
                      {addingMember === (u.sub || u.yumId) ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <UserPlus className="h-3 w-3" />
                      )}
                      Add
                    </button>
                  </div>
                ))}
              </div>
            )}
            {memberSearchDone && memberResults.length === 0 && (
              <p className="mt-2 text-sm text-ink-tertiary">No users found. Users must have logged in at least once to appear here.</p>
            )}
          </div>
        )}
      </section>

      {/* Mentor Feedback — visible to team members and mentors, only when enabled */}
      {mentorsEnabled && (isMember || canGiveFeedback) && (
      <section className="mt-8">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-ink">
          <MessageSquare className="h-5 w-5" /> Mentor Feedback
        </h2>

        {/* Feedback Form (mentors only) */}
        {canGiveFeedback && (
          <form onSubmit={handleSubmitFeedback} className="mt-4 card p-4 border-green-200 bg-green-50/30">
            <h3 className="text-sm font-semibold text-ink">Give Feedback</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs font-medium text-ink-secondary">Section</label>
                <select
                  className="input-base mt-1"
                  value={fbSection}
                  onChange={(e) => setFbSection(e.target.value)}
                >
                  {FEEDBACK_SECTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-ink-secondary">Type</label>
                <select
                  className="input-base mt-1"
                  value={fbType}
                  onChange={(e) => setFbType(e.target.value)}
                >
                  {FEEDBACK_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs font-medium text-ink-secondary">Feedback</label>
              <textarea
                className="input-base mt-1 min-h-[80px]"
                value={fbContent}
                onChange={(e) => setFbContent(e.target.value)}
                placeholder="Write your feedback for the team..."
                maxLength={2000}
              />
              <div className="mt-1 flex items-center justify-between">
                <span className="text-2xs text-ink-tertiary">{fbContent.length}/2000</span>
              </div>
            </div>
            {fbError && (
              <p className="mt-2 text-sm text-red-600">{fbError}</p>
            )}
            {fbSuccess && (
              <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                <Check className="h-3.5 w-3.5" /> Feedback submitted!
              </p>
            )}
            <button
              type="submit"
              className="btn-primary mt-3 inline-flex items-center gap-2"
              disabled={fbSending || !fbContent.trim()}
            >
              {fbSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Submit Feedback
            </button>
          </form>
        )}

        {feedback.length === 0 ? (
          <p className="mt-3 text-ink-secondary">No mentor feedback yet.</p>
        ) : (
          <div className="mt-3 space-y-3">
            {feedback.map((fb) => (
              <div key={fb.feedbackId} className="card p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-ink">{fb.mentorName}</span>
                    <span className="badge bg-accent-subtle text-ink text-2xs">
                      {FEEDBACK_SECTIONS.find((s) => s.value === fb.section)?.label ?? fb.section}
                    </span>
                    <span className={`badge text-2xs ${fb.type === 'blocker' ? 'bg-red-100 text-red-700' : fb.type === 'encouragement' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                      {FEEDBACK_TYPES.find((t) => t.value === fb.type)?.label ?? fb.type}
                    </span>
                  </div>
                  <span className="text-xs text-ink-tertiary">
                    {new Date(fb.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <p className="mt-2 text-sm text-ink-secondary">{fb.content}</p>
              </div>
            ))}
          </div>
        )}
      </section>
      )}
    </div>
  );
}
